import React, { useContext, useMemo, useState } from 'react';
import { TranslationKey } from './../../../../types/translation/TranslationKey';
import { Asset } from '../../../../types/asset/asset';
import { useMutation } from '@apollo/client';
import { Grid } from '@mui/material';
import Modal from '../../../../VentoryUI/components/common/Modal';
import { t } from '../../../../types/translation/Translator';
import AssetInfoPane, { AssetInfoType } from './Panes/AssetInfoPane';
import { AssetInput } from '../../../../types/asset/asset.input';
import {
  AssetTransactionMutations,
  AssignAssetResponse,
  AssignAssetVariables,
  DeleteAssetResponse,
  DeleteAssetVariables,
} from '../../../../graphql/assetTransaction.graphql';
import { AssetTransactionContext } from '../../../../context/AssetTransactionContext';
import { AssetTransaction } from '../../../../types/asset_transaction/assetTransaction';
import UpdateButton from '../../../components/Button/templates/UpdateButton';
import { AssetMutations, UpdateAssetResponse, UpdateAssetVariables } from '../../../../graphql/asset.graphql';
import { Button, CancelButton, DeleteButton } from '../../../components/Button';

interface AssignAssetModalInputProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  asset?: Asset;
}

export default function AssignAssetModal({ open, setOpen, asset }: AssignAssetModalInputProps) {
  if (!asset) return null;

  const { assetTransactions, setAssetTransactions } = useContext(AssetTransactionContext);

  const [input, setInput] = useState<AssetInput>(
    new AssetInput({ ...asset, product: { ...asset.product, quantity: 1, unitQuantity: '1' } }),
  );
  const [error, setError] = useState<string>('');

  const availableQuantity = useMemo(() => {
    return asset.product.quantity;
  }, [asset]);

  const disabled = useMemo(() => {
    return !input.validate() || !input.location || availableQuantity < input.product.quantity;
  }, [input]);

  const [assign, { loading }] = useMutation<AssignAssetResponse, AssignAssetVariables>(
    AssetTransactionMutations.assign,
    {
      onCompleted: res => {
        const transactions = res.assignAsset;
        for (const transaction of transactions) {
          assetTransactions.set(transaction.id, new AssetTransaction(transaction));
        }
        setAssetTransactions(new Map(assetTransactions));
        handleClose();
      },
      onError: res => setError(res.message),
    },
  );

  const [update, { loading: updateLoading }] = useMutation<UpdateAssetResponse, UpdateAssetVariables>(
    AssetMutations.update,
    {
      onCompleted: res => {
        const transactions = res.updateAsset;
        for (const transaction of transactions) {
          assetTransactions.set(transaction.id, new AssetTransaction(transaction));
        }
        setAssetTransactions(new Map(assetTransactions));
        handleClose();
      },
      onError: res => setError(res.message),
    },
  );

  const [remove, { loading: removeLoading }] = useMutation<DeleteAssetResponse, DeleteAssetVariables>(
    AssetTransactionMutations.remove,
    {
      onCompleted: res => {
        const transactions = res.deleteAsset;
        for (const transaction of transactions) {
          assetTransactions.set(transaction.id, new AssetTransaction(transaction));
        }
        setAssetTransactions(new Map(assetTransactions));
        handleClose();
      },
      onError: res => setError(res.message),
    },
  );

  const handleAssign = async () => {
    try {
      await assign({
        variables: {
          asset: input.forUpdate(),
        },
      });
    } catch (e) {
      setError(String(e));
    }
  };

  const handleRemove = async () => {
    try {
      await remove({
        variables: {
          asset: input.forUpdate(),
        },
      });
    } catch (e) {
      setError(String(e));
    }
  };

  const handleClose = () => {
    setInput(new AssetInput({ ...asset, product: { ...asset.product, quantity: 1, unitQuantity: '1' } }));
    setOpen(false);
    setError('');
  };

  const handleUpdate = async () => {
    try {
      await update({
        variables: {
          assets: [input.forUpdate()],
        },
      });
    } catch (e) {
      setError(String(e));
    }
  };

  const footer = () => (
    <Grid container marginTop={'auto'} justifyContent={'space-between'}>
      <Grid item>
        <Grid container columnSpacing={1}>
          <Grid item>
            <UpdateButton onClick={handleUpdate} loading={updateLoading} disabled={loading || removeLoading} />
          </Grid>
          <Grid item alignSelf={'center'}>
            <DeleteButton
              disabled={!input.product.quantity || updateLoading || loading}
              loading={removeLoading}
              text={t(TranslationKey.removeFromAssets).singular.label}
              onClick={handleRemove}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <Grid container columnSpacing={1}>
          <Grid item>
            <CancelButton disabled={loading || removeLoading || updateLoading} onClick={handleClose} />
          </Grid>
          <Grid item>
            <Button
              text={t(TranslationKey.assignAsset).singular.label}
              loading={loading}
              disabled={disabled || removeLoading || updateLoading}
              onClick={handleAssign}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <Modal open={open} error={error} onClose={handleClose} title={t(TranslationKey.assignAsset).singular.label}>
      <AssetInfoPane
        input={input}
        setInput={setInput}
        footer={footer}
        type={AssetInfoType.assign}
        availableQuantity={availableQuantity}
      />
    </Modal>
  );
}
